/**
 * Hybrid V1 <-> V2 request/response adapter (dtrader-c mirror of PU's adapter).
 *
 *   - Rewrite outbound legacy-shaped requests so the V2 options WebSocket accepts them
 *     (e.g. `proposal` needs `underlying_symbol`, not `symbol`), and normalise inbound V2
 *     messages back to the legacy shape downstream dtrader code expects.
 *   - Every alias below is additive (`field` only back-filled when missing) or a documented
 *     V1->V2 rename, so it is safe for all sessions — do NOT gate these on `getAuthFlow()`,
 *     an anonymous V2 session without the `auth_flow` marker would skip them and break.
 *
 * The highest-impact aliases:
 *   - `active_symbols` items: V2 ships `underlying_symbol*` slugs; legacy UI/SmartCharts key on
 *     `symbol`/`display_name`/`*_display_name`/`pip`.
 *   - `proposal_open_contract` (POC) fields: V2 omits the `*_display_value` / `*_tick` /
 *     `entry_tick_time` / `exit_tick_time` / `underlying` names and ships money/spot values as
 *     strings, so we back-fill the legacy names (from `entry_spot`/`exit_spot`/`sell_spot`/
 *     `*_spot_time`) and re-coerce the money/spot fields to numbers (V1 types).
 */

type TPlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is TPlainObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * V2 POC money/spot fields that ship as strings but are numbers in V1, so inbound frames are
 * normalised back to numbers (see `aliasProposalOpenContractFields`). `entry_tick` / `exit_tick` /
 * `sell_spot` are included so their freshly captured aliases are coerced in the same pass.
 */
const POC_NUMERIC_FIELDS = [
    'bid_price',
    'buy_price',
    'profit',
    'payout',
    'entry_spot',
    'entry_tick',
    'exit_spot',
    'exit_tick',
    'sell_spot',
    'sell_price',
    'current_spot',
] as const;

const aliasProposalOpenContractFields = (obj: TPlainObject) => {
    const isMissing = (key: string) =>
        !(key in obj) || obj[key] === undefined || obj[key] === null || obj[key] === '';

    const hasValue = (value: unknown) => value !== undefined && value !== null && String(value) !== '';

    // V2 POC ships only `*_spot` fields; legacy components read `entry_tick` / `exit_tick` /
    // `sell_spot` / `current_spot_display_value` (e.g. digits overlay gates on `entry_tick`,
    // contract cards render `current_spot_display_value`).
    if (isMissing('entry_tick') && hasValue(obj.entry_spot)) {
        obj.entry_tick = obj.entry_spot;
    }

    if (isMissing('exit_tick') && (hasValue(obj.exit_spot) || hasValue(obj.sell_spot))) {
        obj.exit_tick = hasValue(obj.exit_spot) ? obj.exit_spot : obj.sell_spot;
    }

    if (isMissing('sell_spot') && hasValue(obj.exit_spot)) {
        obj.sell_spot = obj.exit_spot;
    }

    if (isMissing('current_spot_display_value') && hasValue(obj.current_spot)) {
        obj.current_spot_display_value = String(obj.current_spot);
    }

    if (isMissing('entry_tick_display_value') && hasValue(obj.entry_spot)) {
        obj.entry_tick_display_value = String(obj.entry_spot);
    }

    if (isMissing('exit_tick_display_value')) {
        const exitSrc = hasValue(obj.exit_spot) ? obj.exit_spot : hasValue(obj.sell_spot) ? obj.sell_spot : undefined;
        if (exitSrc !== undefined) {
            obj.exit_tick_display_value = String(exitSrc);
        }
    }

    if (isMissing('entry_spot_display_value') && hasValue(obj.entry_spot)) {
        obj.entry_spot_display_value = String(obj.entry_spot);
    }

    // V2 renamed the tick timestamps (`entry_spot_time` / `exit_spot_time`); legacy code gates the
    // contract entry spot marker, every chart end/result animation (e.g. digit win/loss circles)
    // and the accumulator live (green) state on `entry_tick_time` / `exit_tick_time`:
    // `chart-marker-helpers.js` returns no entry/end markers without them and `contract-trade-store`
    // keeps `is_accumulator_trade_without_contract` true forever (grey/blue opening look).
    if (isMissing('entry_tick_time') && hasValue(obj.entry_spot_time)) {
        obj.entry_tick_time = obj.entry_spot_time;
    }

    if (isMissing('exit_tick_time') && hasValue(obj.exit_spot_time)) {
        obj.exit_tick_time = obj.exit_spot_time;
    }

    // Positions drawer cards gate their whole live body on `contract_info.underlying`
    // (`{underlying ? contract_el : loader_el}` -> stuck skeleton while the contract runs); V2 ships
    // `underlying_symbol` only. Also feeds crypto checks / display-name lookups.
    if (isMissing('underlying') && hasValue(obj.underlying_symbol)) {
        obj.underlying = obj.underlying_symbol;
    }

    // --- Numeric normalisation (MUST run after the display aliases above) -----------------------
    // V2 returns money/spot values as strings ("-1.20", "956.60") while V1 — and therefore every
    // legacy consumer — uses numbers. Strict checks break on strings, most notably
    // `portfolio-store.onClickSell` which only sells when `typeof bid_price === 'number'`
    // (chart status comparisons and `+profit`-style arithmetic work either way).
    POC_NUMERIC_FIELDS.forEach(field => {
        const value = obj[field];
        if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
            obj[field] = Number(value);
        }
    });
};

/**
 * Deriv V2 `active_symbols` items use `underlying_symbol` / `underlying_symbol_name` field names,
 * but the legacy dtrader UI keys every market lookup on `symbol` / `display_name`
 * (`pickDefaultSymbol`, `getTradeURLParams` symbol validation, market selectors, display names).
 *
 * Without these aliases `pickDefaultSymbol()` cannot find any symbol (returns `undefined`), the
 * trade store ends up with an empty symbol and the request degrades to `{ contracts_for: '1' }`
 * (deriv-api defaults falsy `needsMethodArg` values to `1` and string-coerces them), which the V2
 * options WS rejects with `InputValidationFailed`, leaving the trade UI stuck on its skeleton.
 */
const aliasActiveSymbolFields = (item: TPlainObject) => {
    if (!('symbol' in item) && 'underlying_symbol' in item) {
        item.symbol = item.underlying_symbol;
    }
    if (!('display_name' in item) && 'underlying_symbol_name' in item) {
        item.display_name = item.underlying_symbol_name;
    }

    // SmartCharts sorts/groups markets by these display names (`_processSymbols` calls
    // `submarket_display_name.localeCompare(...)` and would crash on undefined) and derives
    // decimal places from `pip`. V2 ships only the raw slugs + `pip_size`.
    if (!('market_display_name' in item)) item.market_display_name = getDisplayNameFromSlug(item.market);
    if (!('subgroup_display_name' in item)) item.subgroup_display_name = getDisplayNameFromSlug(item.subgroup);
    if (!('submarket_display_name' in item)) item.submarket_display_name = getDisplayNameFromSlug(item.submarket);
    if (!('pip' in item) && typeof item.pip_size === 'number' && item.pip_size > 0) {
        item.pip = item.pip_size;
    }
};

/** Legacy display labels for V2 market slugs the chart/UI shows verbatim. */
const SLUG_DISPLAY_OVERRIDES: Record<string, string> = {
    synthetic_index: 'Derived',
    cryptocurrency: 'Cryptocurrencies',
    indices: 'Stock Indices',
};

const prettifySlug = (slug: string) =>
    slug
        .split('_')
        .map(word => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ');

const getDisplayNameFromSlug = (slug?: unknown) => {
    if (typeof slug !== 'string' || !slug) return '';
    return SLUG_DISPLAY_OVERRIDES[slug] ?? prettifySlug(slug);
};

const aliasActiveSymbols = (response: TPlainObject) => {
    const active_symbols = response.active_symbols;
    if (!Array.isArray(active_symbols)) return;
    active_symbols.forEach(item => {
        if (isPlainObject(item)) aliasActiveSymbolFields(item);
    });
};

/**
 * Deriv V2 `proposal` responses omit `display_value`, which V1 shipped as the display amount of the
 * current contract basis (`basis: 'stake'` -> stake/ask price, `basis: 'payout'` -> payout).
 * The legacy trade store reads it as `proposal_info[trade_type].stake` and passes it as the `price`
 * of the `buy` request — without it every purchase is rejected with `Input validation failed: price`.
 */
const aliasProposalDisplayValue = (response: TPlainObject) => {
    const proposal = response.proposal;
    if (!isPlainObject(proposal) || 'display_value' in proposal) return;

    const echo_req = response.echo_req;
    const basis = isPlainObject(echo_req) ? echo_req.basis : undefined;
    const source = basis === 'payout' ? proposal.payout ?? proposal.ask_price : proposal.ask_price;

    if (source === undefined || source === null) return;
    proposal.display_value = String(source);
};

/**
 * Deriv V2 `contracts_for.available` items omit `start_type` (every Options V2 contract starts
 * immediately, i.e. `spot`). Legacy duration handling indexes `durations.min_max[contract.start_type]`
 * and throws `Cannot set properties of undefined` when it is missing, so back-fill `spot`.
 */
const aliasContractsForFields = (response: TPlainObject) => {
    const contracts_for = response.contracts_for;
    if (!isPlainObject(contracts_for)) return;
    const available = contracts_for.available;
    if (!Array.isArray(available)) return;
    available.forEach(item => {
        if (isPlainObject(item) && !('start_type' in item)) item.start_type = 'spot';
    });
};

/**
 * V2 `buy`/`sell` declare `price` as a number (`buy_request.schema.json` / `sell_request.schema.json`)
 * while legacy code paths hand over display strings (e.g. `display_value: '10'`). Coerce, and leave
 * numbers untouched so the function stays idempotent for the double-adaptation in socket_base.
 */
const coerceNumericPrice = (request: TPlainObject, method: 'buy' | 'sell') => {
    if (!(method in request)) return request;
    if (typeof request.price !== 'string') return request;
    const price = Number(request.price);
    if (Number.isFinite(price)) request.price = price;
    return request;
};

export const adaptRequestForActiveFlow = (request: unknown): unknown => {
    if (!isPlainObject(request)) return request;

    let adapted = request;

    if ('proposal' in adapted) {
        // V2 `proposal` requires `underlying_symbol` and rejects `symbol`
        // (see DERIV-API-SCHEMA/schemas/proposal_request.schema.json, additionalProperties: false).
        // V1's forward-start `date_start` no longer exists either: V2 options contracts always
        // start immediately.
        adapted = { ...adapted };
        if ('symbol' in adapted && !('underlying_symbol' in adapted)) {
            adapted.underlying_symbol = adapted.symbol;
            delete adapted.symbol;
        }
        if ('date_start' in adapted) {
            delete adapted.date_start;
        }
    }

    if ('buy' in adapted || 'sell' in adapted) {
        adapted = adapted === request ? { ...adapted } : adapted;
        coerceNumericPrice(adapted, 'buy');
        coerceNumericPrice(adapted, 'sell');
    }

    return adapted;
};

export const adaptResponseForActiveFlow = (response: unknown): unknown => {
    if (!isPlainObject(response)) return response;

    // The aliases below are purely additive: they only back-fill legacy field names when absent,
    // so they are safe (no-ops) for both OAuth2/V2 and legacy sessions and must NOT be gated on
    // `getAuthFlow()` — an anonymous V2 session without the `auth_flow` marker would otherwise
    // skip them and break symbol picking (see aliasActiveSymbolFields docs).
    const poc = response.proposal_open_contract;
    if (isPlainObject(poc)) {
        aliasProposalOpenContractFields(poc);
    }

    aliasProposalDisplayValue(response);
    aliasActiveSymbols(response);
    aliasContractsForFields(response);

    return response;
};
